/**
 * Layout Mapper Utilities
 *
 * Maps Figma's internal stack* layout properties to web-friendly flexbox properties.
 */

import type {
  AlignmentProperties,
  ChildLayoutProperties,
  LayoutProperties,
  PaddingValues,
  PositionProperties,
  SizingIntent,
  SizingProperties,
} from '../types/component-tree.js'

/**
 * Map Figma's stackMode to web-friendly layout mode
 */
export function mapLayoutMode(stackMode: string | undefined): 'horizontal' | 'vertical' | 'none' {
  if (stackMode === 'HORIZONTAL') return 'horizontal'
  if (stackMode === 'VERTICAL') return 'vertical'
  return 'none'
}

/**
 * Extract padding from Figma's stack padding properties
 *
 * Figma uses:
 * - stackHorizontalPadding (left + right default)
 * - stackVerticalPadding (top + bottom default)
 * - stackPaddingLeft (override)
 * - stackPaddingRight (override)
 * - stackPaddingTop (override)
 * - stackPaddingBottom (override)
 */
export function extractPadding(props: any): PaddingValues | undefined {
  const hasAnyPadding =
    props.stackHorizontalPadding !== undefined ||
    props.stackVerticalPadding !== undefined ||
    props.stackPaddingTop !== undefined ||
    props.stackPaddingRight !== undefined ||
    props.stackPaddingBottom !== undefined ||
    props.stackPaddingLeft !== undefined

  if (!hasAnyPadding) {
    return undefined
  }

  const horizontalDefault = props.stackHorizontalPadding ?? 0
  const verticalDefault = props.stackVerticalPadding ?? 0

  return {
    top: props.stackPaddingTop ?? verticalDefault,
    right: props.stackPaddingRight ?? horizontalDefault,
    bottom: props.stackPaddingBottom ?? verticalDefault,
    left: props.stackPaddingLeft ?? horizontalDefault,
  }
}

/**
 * Map Figma's stackPrimaryAlignItems to web alignment value
 */
export function mapMainAlignment(
  stackPrimaryAlignItems: string | undefined,
): 'start' | 'center' | 'end' | 'space-between' {
  switch (stackPrimaryAlignItems) {
    case 'MIN':
      return 'start'
    case 'CENTER':
      return 'center'
    case 'MAX':
      return 'end'
    case 'SPACE_BETWEEN':
      return 'space-between'
    default:
      return 'start'
  }
}

/**
 * Map Figma's stackCounterAlignItems to web alignment value
 */
export function mapCrossAlignment(
  stackCounterAlignItems: string | undefined,
): 'start' | 'center' | 'end' | 'stretch' {
  switch (stackCounterAlignItems) {
    case 'MIN':
      return 'start'
    case 'CENTER':
      return 'center'
    case 'MAX':
      return 'end'
    case 'BASELINE':
      return 'start' // Closest equivalent
    default:
      return 'stretch'
  }
}

/**
 * Extract alignment properties from Figma stack properties
 */
export function extractAlignment(props: any): AlignmentProperties | undefined {
  if (!props.stackPrimaryAlignItems && !props.stackCounterAlignItems) {
    return undefined
  }

  return {
    main: mapMainAlignment(props.stackPrimaryAlignItems),
    cross: mapCrossAlignment(props.stackCounterAlignItems),
  }
}

/**
 * Map Figma's sizing mode to web-friendly value
 */
export function mapSizingMode(sizingMode: string | undefined): 'hug' | 'fill' | 'fixed' {
  switch (sizingMode) {
    case 'RESIZE_TO_FIT':
    case 'RESIZE_TO_FIT_WITH_IMPLICIT_SIZE':
      return 'hug'
    case 'FILL':
      return 'fill'
    case 'FIXED':
    default:
      return 'fixed'
  }
}

/**
 * Extract sizing properties from Figma stack properties
 */
export function extractSizing(props: any): SizingProperties | undefined {
  const hasAnySizing =
    props.stackPrimarySizing !== undefined ||
    props.stackCounterSizing !== undefined

  // If no sizing properties, return undefined (not an auto-layout)
  if (!hasAnySizing) {
    return undefined
  }

  // Return sizing with proper defaults
  return {
    primary: mapSizingMode(props.stackPrimarySizing),
    counter: mapSizingMode(props.stackCounterSizing),
  }
}

/**
 * Map Figma's stackChildAlignSelf to web alignment value
 */
export function mapChildAlignSelf(
  stackChildAlignSelf: string | undefined,
): 'auto' | 'start' | 'center' | 'end' | 'stretch' {
  switch (stackChildAlignSelf) {
    case 'MIN':
      return 'start'
    case 'CENTER':
      return 'center'
    case 'MAX':
      return 'end'
    case 'STRETCH':
      return 'stretch'
    case 'BASELINE':
      return 'start' // Closest equivalent
    default:
      return 'auto'
  }
}

/**
 * Extract child layout properties
 */
export function extractChildLayout(props: any): ChildLayoutProperties | undefined {
  const hasGrow = props.stackChildPrimaryGrow !== undefined
  const hasAlignSelf = props.stackChildAlignSelf !== undefined

  if (!hasGrow && !hasAlignSelf) {
    return undefined
  }

  const child: ChildLayoutProperties = {}
  if (hasGrow) child.grow = props.stackChildPrimaryGrow
  if (hasAlignSelf) child.alignSelf = mapChildAlignSelf(props.stackChildAlignSelf)
  return child
}

/**
 * Extract position from transform matrix
 */
export function extractPosition(transform: any): PositionProperties | undefined {
  if (!transform) {
    return undefined
  }

  return {
    x: transform.m02 ?? 0,
    y: transform.m12 ?? 0,
  }
}

/**
 * Determine sizing intent based on node context
 *
 * Detection rules:
 * 1. Child with grow: 1 → "flexible" (fills available space)
 * 2. Sizing mode "hug" → "hug" (shrink to content)
 * 3. Sizing mode "fill" → "flexible" (fills container)
 * 4. Default → "fixed" (exact dimensions)
 *
 * Axis mapping for auto-layout:
 * - HORIZONTAL: primary=width, counter=height
 * - VERTICAL: primary=height, counter=width
 *
 * Note: "max" is never auto-detected. Implementers should decide when to use
 * responsive max-width behavior based on their context.
 *
 * @param _isRoot - Whether this is the root/top-level node (kept for API compatibility but not used)
 * @param sizing - Container sizing properties (primary/counter)
 * @param childLayout - Child layout properties (grow, alignSelf)
 * @param layoutMode - Container layout mode (HORIZONTAL/VERTICAL/none)
 * @param axis - Which axis ('width' or 'height')
 */
export function determineSizingIntent(
  _isRoot: boolean,  // Kept for API compatibility but not used (isRoot is added to DimensionValue directly)
  sizing?: SizingProperties,
  childLayout?: ChildLayoutProperties,
  layoutMode?: string,
  axis?: 'width' | 'height',
): SizingIntent {
  // Rule 1: Child with grow: 1 → "flexible"
  if (childLayout?.grow === 1) {
    return 'flexible'
  }

  // Rule 2: Check sizing mode for this axis
  // For horizontal layout: primary = width, counter = height
  // For vertical layout: primary = height, counter = width
  let sizingMode: 'hug' | 'fill' | 'fixed' | undefined

  if (layoutMode === 'HORIZONTAL') {
    sizingMode = axis === 'width' ? sizing?.primary : sizing?.counter
  } else if (layoutMode === 'VERTICAL') {
    sizingMode = axis === 'height' ? sizing?.primary : sizing?.counter
  } else {
    // For non-auto-layout, use primary
    sizingMode = sizing?.primary
  }

  if (sizingMode === 'hug') {
    return 'hug'
  }

  if (sizingMode === 'fill') {
    return 'flexible'
  }

  // Default: fixed
  return 'fixed'
}

/**
 * Extract complete layout properties from Figma node
 * @param props - Figma node properties
 * @param isRoot - Whether this is the root/top-level node (for sizing intent detection)
 * @param includeLayoutDimensions - Whether to include dimensional/spacing values (gap, padding, width, height)
 */
export function extractLayout(props: any, isRoot = false, includeLayoutDimensions = true): LayoutProperties | undefined {
  // Check if node has any layout properties
  const hasLayoutMode = props.stackMode !== undefined
  const hasSize = props.size !== undefined
  const hasChildLayout = props.stackChildPrimaryGrow !== undefined || props.stackChildAlignSelf !== undefined

  if (!hasLayoutMode && !hasSize && !hasChildLayout) {
    return undefined
  }

  const layout: LayoutProperties = {}

  // Container auto-layout properties
  if (hasLayoutMode) {
    layout.mode = mapLayoutMode(props.stackMode)

    // Only include spacing/dimensions if includeLayoutDimensions is true
    if (includeLayoutDimensions) {
      if (props.stackSpacing !== undefined) layout.gap = props.stackSpacing
      const padding = extractPadding(props)
      if (padding) layout.padding = padding
    }

    // Always include structural alignment
    const align = extractAlignment(props)
    if (align) layout.align = align

    const sizing = extractSizing(props)
    if (sizing) layout.sizing = sizing
  }

  // Dimensions with sizing intent
  if (props.size && includeLayoutDimensions) {
    const sizing = extractSizing(props)
    const childLayout = extractChildLayout(props)
    const layoutMode = props.stackMode

    layout.width = {
      value: props.size.x,
      sizingIntent: determineSizingIntent(isRoot, sizing, childLayout, layoutMode, 'width'),
      ...(isRoot && { isRoot: true }),  // Include isRoot flag for context (only if true)
    }

    layout.height = {
      value: props.size.y,
      sizingIntent: determineSizingIntent(isRoot, sizing, childLayout, layoutMode, 'height'),
      ...(isRoot && { isRoot: true }),  // Include isRoot flag for context (only if true)
    }
  }

  // Position
  const position = extractPosition(props.transform)
  if (position) layout.position = position

  // Child layout properties
  const child = extractChildLayout(props)
  if (child) layout.child = child

  return layout
}
